import { addSessionEvent } from '@/db/session-events';
import { createInviteTokenId } from '@/schemas';
import type { InviteTokenId, SessionId, UserId } from '@/schemas';
import { createSystemEvent } from '../../session-helpers';
import type { QueueDependencies } from '../queue-utils';

type InviteTokenOperationParams = QueueDependencies & {
  sessionId: SessionId;
  userId: UserId | undefined;
};

type GenerateInviteTokenParams = InviteTokenOperationParams & {
  previousToken: InviteTokenId | undefined;
};

type RevokeInviteTokenParams = InviteTokenOperationParams & {
  previousToken: InviteTokenId;
};

type ToggleInviteLinkParams = InviteTokenOperationParams & {
  enable: boolean;
};

/**
 * Generate or regenerate an invite token for a session
 */
export const enqueueGenerateInviteToken = async ({
  operationQueue,
  refreshSessionFromEvents,
  updateUndoRedoAvailability,
  sessionId,
  userId,
  previousToken,
}: GenerateInviteTokenParams): Promise<InviteTokenId> => {
  const newToken = createInviteTokenId();

  await operationQueue.enqueue<InviteTokenId>({
    id: crypto.randomUUID(),
    type: 'generateInviteToken',
    dedupKey: `invite:generate:${sessionId}`,
    priority: 6,
    optimisticUpdate: () => {
      // Token state is stored in metadata, not session directly
      // No optimistic update needed for session state
    },
    rollback: async () => {
      await refreshSessionFromEvents(sessionId);
    },
    execute: async () => {
      // If there was a previous token, revoke it first
      if (previousToken !== undefined) {
        await addSessionEvent(
          createSystemEvent(
            sessionId,
            'InviteTokenRevoked',
            {
              previousToken,
              revokedBy: userId,
              reason: 'regenerated',
            },
            userId,
          ),
        );
      }

      // Generate new token
      await addSessionEvent(
        createSystemEvent(
          sessionId,
          'InviteTokenGenerated',
          {
            inviteToken: newToken,
            generatedBy: userId,
          },
          userId,
        ),
      );

      await updateUndoRedoAvailability(sessionId);
      await refreshSessionFromEvents(sessionId);
      return newToken;
    },
  });

  return newToken;
};

/**
 * Revoke the current invite token for a session
 */
export const enqueueRevokeInviteToken = async ({
  operationQueue,
  refreshSessionFromEvents,
  updateUndoRedoAvailability,
  sessionId,
  userId,
  previousToken,
}: RevokeInviteTokenParams): Promise<void> => {
  await operationQueue.enqueue<void>({
    id: crypto.randomUUID(),
    type: 'revokeInviteToken',
    dedupKey: `invite:revoke:${sessionId}`,
    priority: 6,
    optimisticUpdate: () => {
      // Token state is stored in metadata, not session directly
      // No optimistic update needed for session state
    },
    rollback: async () => {
      await refreshSessionFromEvents(sessionId);
    },
    execute: async () => {
      await addSessionEvent(
        createSystemEvent(
          sessionId,
          'InviteTokenRevoked',
          {
            previousToken,
            revokedBy: userId,
            reason: 'manual',
          },
          userId,
        ),
      );

      await updateUndoRedoAvailability(sessionId);
      await refreshSessionFromEvents(sessionId);
    },
  });
};

/**
 * Enable or disable invite links for a session
 */
export const enqueueToggleInviteLink = async ({
  operationQueue,
  refreshSessionFromEvents,
  updateUndoRedoAvailability,
  sessionId,
  userId,
  enable,
}: ToggleInviteLinkParams): Promise<void> => {
  const eventType = enable ? 'InviteLinkEnabled' : 'InviteLinkDisabled';
  const payload = enable ? { enabledBy: userId } : { disabledBy: userId };

  await operationQueue.enqueue<void>({
    id: crypto.randomUUID(),
    type: enable ? 'enableInviteLink' : 'disableInviteLink',
    dedupKey: `invite:${enable ? 'enable' : 'disable'}:${sessionId}`,
    priority: 6,
    optimisticUpdate: () => {
      // Invite link state is tracked in metadata
      // No optimistic update needed for session state
    },
    rollback: async () => {
      await refreshSessionFromEvents(sessionId);
    },
    execute: async () => {
      await addSessionEvent(createSystemEvent(sessionId, eventType, payload, userId));

      await updateUndoRedoAvailability(sessionId);
      await refreshSessionFromEvents(sessionId);
    },
  });
};
